import React, {useEffect, useState} from "react";
import type {Payment, PayinRequisites} from "../../types";
import {belowTransferMinimum, formatAmount, formatCard, formatRubles, selectableRange} from "../../utils";
import {transferLimit, type TraderPayinTerms} from "../../utils/portalInTerms";
import {equalPayinParts, getRemainingAmount, humanSize, formatRubInput, parseRubToKopeks} from "./logic";
import {PayinRequisiteInfo} from "../PayinParts";
import type {WorkflowStageContext} from "./stage";
import {RECEIPT_FILE_ACCEPT, RECEIPT_FILE_HINT} from "./types";
import type {AmountAnalysis, RateAnalysis, ReceiptDraft} from "./types";

// StageState — где трейдер находится в ленте этапов.
//
// «done» рисует галочку вместо цифры: пройденный этап должен отличаться от
// непройденного с одного взгляда, иначе лента из трёх одинаковых блоков не
// говорит, что уже сделано. Номер при этом не исчезает из смысла — порядок
// этапов задаёт сама лента, — но повторять цифру у сделанного нечего.
//
// Состояние необязательное: у сценариев без ленты секции как были, так и
// остаются, без классов и без галочек.
export type StageState = "done" | "current" | "ahead";

export function SectionTitle({children, hint, number, state}: {
    children: React.ReactNode;
    hint?: string | null;
    number?: string;
    state?: StageState;
}): JSX.Element {
    const suffix = state ? ` ${state}` : "";
    return (
        <div className={`details-section-title${suffix}`}>
            {number && <span className={`details-step${suffix}`}>{state === "done" ? "✓" : number}</span>}
            <strong>{children}</strong>
            {hint && <span>{hint}</span>}
        </div>
    );
}

// stageSectionClass — приглушение секции этапа, до которого работа не дошла.
export function stageSectionClass(state?: StageState): string {
    return state === "ahead" ? "details-section stage-ahead" : "details-section";
}

function InfoRow({label, value, mono}: { label: string; value: string | number | null | undefined; mono?: boolean }): JSX.Element {
    return (
        <div className="details-info-row">
            <span>{label}</span>
            <strong className={mono ? "mono" : ""}>{value == null || value === "" ? "-" : value}</strong>
        </div>
    );
}

export function RequisitesStage({number, title, hint, context, readOnly, state}: {
    number: string;
    title: string;
    hint: string;
    context: WorkflowStageContext;
    // readOnly — этап уже пройден и измениться не может. Форма выдачи
    // реквизитов на такой карточке обещала бы действие, которого сервер больше
    // не примет, поэтому её место занимает объяснение. По умолчанию выключено:
    // у всех сценариев, кроме пройденной рублёвой части обмена, этап живой.
    readOnly?: boolean;
    state?: StageState;
}): JSX.Element {
    const existing = context.payment.payinRequisites;
    const {draft, onChange, onSave, submitting, canSave} = context.requisites;

    return (
        <section className={stageSectionClass(state)}>
            <SectionTitle number={number} hint={hint} state={state}>{title}</SectionTitle>
            {existing?.parts?.length ? (
                <div className="payin-parts-list">{existing.parts.map((part, index) => <div className="details-info-card" key={index}>
                    <strong>Реквизит {index + 1} · {formatAmount(part.amount)} RUB</strong>
                    <PayinRequisiteInfo requisites={part.requisites}/>
                </div>)}</div>
            ) : existing ? (
                <div className="details-info-card requisites-existing-card">
                    {existing.kind === "sbp" ? <>
                        <InfoRow label="Телефон СБП" value={existing.phone} mono/>
                        <InfoRow label="Получатель" value={existing.recipient}/>
                    </> : <>
                        <InfoRow label="Номер" value={formatCard(existing.cardNumber)} mono/>
                        <InfoRow label="Получатель" value={existing.cardHolder}/>
                    </>}
                    <InfoRow label="Банк" value={existing.bankName}/>
                    {existing.kind !== "sbp" && <InfoRow label="Код банка" value={existing.bankCode} mono/>}
                    {existing.kind !== "sbp" && <InfoRow label="Назначение" value={existing.payment}/>}
                </div>
            ) : readOnly ? (
                <div className="details-lock-hint" role="status">Реквизиты по этой заявке не выдавались.</div>
            ) : (
                <PayinRequisitesEditor
                    total={context.requisites.total ?? getRemainingAmount(context.payment)}
                    terms={context.requisites.terms ?? null}
                    orderAmount={context.requisites.orderAmount ?? null}
                    allowParts={context.payment.workflowKind === "standard_payin"}
                    draft={draft}
                    onChange={onChange}
                    onSave={onSave}
                    submitting={submitting}
                    canSave={canSave}
                />
            )}
        </section>
    );
}

type RequisitesOrderAmount = {value: string; onChange: (next: string) => void; problem: string};

function PayinRequisitesEditor(props: {draft: PayinRequisites; onChange: (value: PayinRequisites) => void; onSave: () => void; submitting: boolean; canSave: boolean; total: number; allowParts: boolean; terms: TraderPayinTerms | null; orderAmount: RequisitesOrderAmount | null}): JSX.Element {
    const {draft, onChange, total, allowParts, terms, orderAmount} = props;
    const parts = draft.parts;
    // Мерчант назвал, до скольких переводов может оплатить: трейдер выдаёт от одного
    // реквизита до этого числа, каждая часть не меньше минимума. Добавление и
    // удаление делят сумму поровну заново — набранные реквизиты остаются на местах.
    const limit = terms?.transferCount ?? null;
    const maxParts = transferLimit(terms, total);
    const blank = (): PayinRequisites => ({cardNumber: "", cardHolder: "", bankName: ""});
    const updateParts = (next: NonNullable<PayinRequisites["parts"]>) => onChange({...blank(), parts: next});
    const sum = parts?.reduce((value, part) => value + part.amount, 0) ?? total;
    const removePart = (index: number) => {
        const next = parts!.filter((_, i) => i !== index);
        if (next.length === 1) onChange(next[0].requisites);
        else if (limit !== null) onChange(equalPayinParts(next.length, total, next));
        else updateParts(next);
    };
    const addPart = () => {
        if (limit !== null) {
            const current = parts?.length ? parts : [{amount: total, requisites: draft}];
            onChange(equalPayinParts(current.length + 1, total, current));
        } else {
            updateParts(parts?.length ? [...parts, {amount: Math.max(0, total - sum), requisites: blank()}] : [{amount: total, requisites: draft}, {amount: 0, requisites: blank()}]);
        }
    };
    return <div className="payin-parts-list">
        {terms && orderAmount && <OrderAmountField terms={terms} amount={orderAmount}/>}
        {limit !== null && terms?.minTransfer != null && <div className="details-lock-hint" role="status">Выдайте от 1 до {limit} реквизитов, каждый на сумму не меньше {formatRubles(terms.minTransfer)} ₽.</div>}
        {parts?.length ? <>
            {parts.map((part, index) => <div className="details-info-card payin-part-editor" key={index}>
                <div className="requisites-actions"><strong>Реквизит {index + 1}</strong><button type="button" className="btn btn-ghost btn-sm" disabled={props.submitting} onClick={() => removePart(index)}>Удалить</button></div>
                <PartAmount amount={part.amount} minimum={terms?.minTransfer ?? null} terms={terms} onChange={(amount) => updateParts(parts.map((p, i) => i === index ? {...p, amount} : p))}/>
                <PayinRequisitesForm {...props} draft={part.requisites} onChange={(requisites) => updateParts(parts.map((p, i) => i === index ? {...p, requisites} : p))} showSave={false}/>
            </div>)}
            <div className={sum === total ? "details-lock-hint" : "receipt-error"} role="status">Распределено {formatRubInput(sum)} из {formatRubInput(total)} RUB. Сумма частей должна совпадать с суммой заявки.</div>
        </> : <PayinRequisitesForm {...props} showSave={false}/>}
        <div className="requisites-actions">
            {allowParts && <button className="btn btn-ghost" type="button" disabled={props.submitting || (parts?.length ?? 1) >= maxParts} onClick={addPart}>Добавить реквизит</button>}
            <button className="btn btn-primary" type="button" onClick={props.onSave} disabled={!props.canSave || props.submitting}>{props.submitting ? "Сохраняем..." : "Сохранить реквизиты"}</button>
        </div>
        {parts?.length && <small>Все реквизиты будут выданы одновременно. После выдачи изменить их нельзя.</small>}
    </div>;
}

// OrderAmountField — выбор суммы заявки из диапазона мерчанта. Сумму можно
// сменить до выдачи реквизитов; после выдачи она фиксируется, и поле исчезает.
function OrderAmountField({terms, amount}: {terms: TraderPayinTerms; amount: RequisitesOrderAmount}): JSX.Element | null {
    const bounds = selectableRange(terms);
    if (!bounds) return null;
    return <div className="payin-order-amount">
        <label className={amount.problem ? "requisites-field requisites-field-invalid" : "requisites-field"}>
            <span>Сумма заявки, ₽ *</span>
            <input inputMode="decimal" autoComplete="off" value={amount.value} aria-invalid={amount.problem ? true : undefined} onChange={(event) => amount.onChange(event.target.value)}/>
        </label>
        <small className="payin-order-amount-hint">Выберите сумму от {formatRubles(bounds.low)} до {formatRubles(bounds.high)} ₽.</small>
        {amount.problem && <small className="requisites-field-error" role="alert">{amount.problem}</small>}
    </div>;
}

function PartAmount({amount, onChange, minimum, terms}: {amount: number; onChange: (amount: number) => void; minimum: number | null; terms: TraderPayinTerms | null}): JSX.Element {
    const [raw, setRaw] = useState(() => amount > 0 ? formatRubInput(amount) : "");
    useEffect(() => { if ((parseRubToKopeks(raw) ?? 0) !== amount) setRaw(amount > 0 ? formatRubInput(amount) : ""); }, [amount, raw]);
    const below = minimum !== null && belowTransferMinimum(terms, amount);
    return <label className={below ? "requisites-field requisites-field-invalid" : "requisites-field"}>
        <span>Сумма, RUB *</span>
        <input inputMode="decimal" value={raw} aria-invalid={below ? true : undefined} onChange={(event) => {setRaw(event.target.value); onChange(parseRubToKopeks(event.target.value) ?? 0);}}/>
        {below && <small className="requisites-field-error" role="alert">Не меньше {formatRubles(minimum)} ₽ — столько просит мерчант.</small>}
    </label>;
}

export function PayinRequisitesForm({
                                    draft,
                                    onChange,
                                    onSave,
                                    submitting,
                                    canSave,
                                    showSave = true,
                                }: {
    draft: PayinRequisites;
    onChange: (next: PayinRequisites) => void;
    onSave: () => void;
    submitting: boolean;
    canSave: boolean;
    showSave?: boolean;
}): JSX.Element {
    const update = (patch: Partial<PayinRequisites>) => onChange({...draft, ...patch});

    return (
        <div className="requisites-form">
            <div className="requisites-grid">
                <label className="requisites-field">
                    <span>Тип реквизита *</span>
                    <select value={draft.kind ?? "card"} onChange={(event) => update({kind: event.target.value as "card" | "sbp"})}>
                        <option value="card">Карта</option>
                        <option value="sbp">СБП</option>
                    </select>
                </label>
                {(draft.kind ?? "card") === "sbp" ? <>
                    <RequisitesInput label="Телефон СБП" value={draft.phone ?? ""} onChange={(value) => update({phone: value})} mono required />
                    <RequisitesInput label="Получатель" value={draft.recipient ?? ""} onChange={(value) => update({recipient: value})} required />
                </> : <>
                <RequisitesInput
                    label="Номер карты"
                    value={draft.cardNumber}
                    onChange={(value) => update({cardNumber: value})}
                    mono
                    required
                />
                <RequisitesInput
                    label="Получатель"
                    value={draft.cardHolder}
                    onChange={(value) => update({cardHolder: value})}
                    required
                />
                </>}
                <RequisitesInput
                    label="Банк"
                    value={draft.bankName}
                    onChange={(value) => update({bankName: value})}
                    required
                />
                {(draft.kind ?? "card") === "card" && <RequisitesInput
                    label="Код банка"
                    value={draft.bankCode ?? ""}
                    onChange={(value) => update({bankCode: value})}
                    mono
                />}
                <RequisitesInput
                    label="Назначение / телефон"
                    value={draft.payment ?? ""}
                    onChange={(value) => update({payment: value})}
                />
            </div>
            {showSave && <div className="requisites-actions">
                <button className="btn btn-primary" type="button" onClick={onSave} disabled={!canSave || submitting}>
                    {submitting ? "Сохраняем..." : "Сохранить реквизиты"}
                </button>
            </div>}
        </div>
    );
}

function RequisitesInput({
                             label,
                             value,
                             onChange,
                             mono,
                             required,
                         }: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    mono?: boolean;
    required?: boolean;
}): JSX.Element {
    return (
        <label className="requisites-field">
            <span>{label}{required ? " *" : ""}</span>
            <input
                className={mono ? "mono" : ""}
                type="text"
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        </label>
    );
}

export function ReceiptStage({number, title, hint, context, disabled = false}: {
    number: string;
    title: string;
    hint: string;
    context: WorkflowStageContext;
    disabled?: boolean;
}): JSX.Element {
    return (
        <section className="details-section">
            <SectionTitle number={number} hint={hint}>{title}</SectionTitle>
            <ReceiptUpload
                inputRef={context.receipt.inputRef}
                receipts={context.receipt.drafts}
                error={context.receipt.error}
                hasExistingReceipt={Boolean(context.payment.receiptId)}
                existingReceiptUrl={context.payment.receiptUrl}
                downloadingExistingReceipt={context.receipt.downloadingExisting}
                onFiles={context.receipt.onFiles}
                onClear={context.receipt.onClear}
                onDownloadExistingReceipt={context.receipt.onDownloadExisting}
                disabled={disabled}
            />
        </section>
    );
}

function dropzoneTitle(chosen: number, hasExistingReceipt: boolean): string {
    if (chosen > 0) return "Добавить ещё чек";
    if (hasExistingReceipt) return "Заменить чек";
    return "Перетащите чеки или выберите файлы";
}

// ReceiptUpload держит список выбранных чеков.
//
// Чеков может быть сколько угодно у любой заявки (UOF-02): платёж один, на всю
// сумму заявки, а переводов за ним несколько — 20 + 10 + 10, три файла. Поэтому
// после выбора первого файла зона выбора остаётся на месте, а не исчезает.
function ReceiptUpload({
                           inputRef,
                           receipts,
                           error,
                           hasExistingReceipt,
                           existingReceiptUrl,
                           downloadingExistingReceipt,
                           onFiles,
                           onClear,
                           onDownloadExistingReceipt,
                           disabled = false,
                       }: {
    inputRef: React.RefObject<HTMLInputElement>;
    receipts: ReceiptDraft[];
    error: string;
    hasExistingReceipt: boolean;
    existingReceiptUrl?: string | null;
    downloadingExistingReceipt: boolean;
    onFiles: (files: FileList | null) => void;
    onClear: (index: number) => void;
    onDownloadExistingReceipt: () => void;
    disabled?: boolean;
}): JSX.Element {
    const [dragging, setDragging] = useState(false);

    const chosen = receipts.length > 0 && (
        <div className="receipt-ready-list">
            {receipts.map((receipt, index) => (
                <div className="receipt-ready-card" key={`${receipt.fileName}-${index}`}>
                    <div className="receipt-file-icon">ЧЕК</div>
                    <div className="receipt-file-main">
                        <strong>{receipt.fileName}</strong>
                        <span>
                            {humanSize(receipt.fileSize)} ·{" "}
                            {receipts.length > 1 ? `чек ${index + 1} из ${receipts.length}` : "новый чек"} готов к отправке
                        </span>
                    </div>
                    <button className="btn btn-ghost btn-sm" disabled={disabled} onClick={() => window.open(receipt.objectUrl, "_blank", "noopener,noreferrer")}>
                        Просмотреть
                    </button>
                    <button className="btn btn-ghost btn-sm danger-text" disabled={disabled} onClick={() => onClear(index)}>
                        Удалить
                    </button>
                </div>
            ))}
        </div>
    );

    return (
        <div>
            {chosen}
            <input
                ref={inputRef}
                type="file"
                accept={RECEIPT_FILE_ACCEPT}
                multiple
                hidden
                disabled={disabled}
                onChange={(event) => onFiles(event.target.files)}
            />
            <div
                className={`receipt-dropzone ${dragging ? "dragging" : ""}`}
                role="button"
                tabIndex={0}
                aria-disabled={disabled ? "true" : undefined}
                onClick={() => { if (!disabled) inputRef.current?.click(); }}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (!disabled) inputRef.current?.click();
                    }
                }}
                onDragOver={(event) => {
                    event.preventDefault();
                    if (disabled) return;
                    setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                    event.preventDefault();
                    if (disabled) return;
                    setDragging(false);
                    onFiles(event.dataTransfer.files);
                }}
            >
                <div className="receipt-drop-icon">ЧЕК</div>
                <strong>{dropzoneTitle(receipts.length, hasExistingReceipt)}</strong>
                <span>{RECEIPT_FILE_HINT} · можно несколько</span>
            </div>
            {receipts.length === 0 && (
                <div className="receipt-existing-note">
                    Платили несколькими переводами — приложите чек на каждый: сумму вводите общую.
                </div>
            )}
            {hasExistingReceipt && receipts.length === 0 && (
                <div className="receipt-existing-note">К платежу уже приложен чек — можно завершить без повторной загрузки.</div>
            )}
            {hasExistingReceipt && existingReceiptUrl && (
                <button
                    className="btn btn-ghost btn-sm"
                    onClick={(event) => {
                        event.stopPropagation();
                        onDownloadExistingReceipt();
                    }}
                    disabled={disabled || downloadingExistingReceipt}
                >
                    {downloadingExistingReceipt ? "Downloading..." : "Download receipt"}
                </button>
            )}
            {error && <div className="receipt-error">{error}</div>}
        </div>
    );
}

export function AmountAndRateStage({number, title, isIncome, context}: {
    number: string;
    title: string;
    isIncome: boolean;
    context: WorkflowStageContext;
}): JSX.Element {
    return (
        <section className="details-section">
            <SectionTitle number={number}>{title}</SectionTitle>
            <AmountAndRateFields isIncome={isIncome} context={context}/>
        </section>
    );
}

// AmountAndRateFields — те же два поля без собственной секции и заголовка.
//
// Понадобилось лентой этапов: у заявки-обмена сумма и курс не отдельный этап, а
// часть этапа «Оплата клиента» — трейдер подтверждает ими ровно то, что прислал
// клиент. Своя секция посреди чужого этапа рвала бы нумерацию ленты. Обёртка
// выше сохранена целиком, поэтому у остальных сценариев ничего не меняется.
export function AmountAndRateFields({isIncome, context}: {
    isIncome: boolean;
    context: WorkflowStageContext;
}): JSX.Element {
    return (
        <>
            <AmountInput
                payment={context.payment}
                isIncome={isIncome}
                value={context.amount.value}
                onChange={context.amount.onChange}
                analysis={context.amount.analysis}
            />
            <RateInput
                value={context.rate.value}
                onChange={context.rate.onChange}
                analysis={context.rate.analysis}
                fixedRate={context.rate.fixed}
            />
        </>
    );
}

function AmountInput({
                         payment,
                         isIncome,
                         value,
                         onChange,
                         analysis,
                     }: {
    payment: Payment;
    isIncome: boolean;
    value: string;
    onChange: (next: string) => void;
    analysis: AmountAnalysis;
}): JSX.Element {
    // The amount is derived from the current flow; it cannot be edited here.
    const hint = analysis.overpay
        ? `Сумма превышает остаток заявки (${formatAmount(analysis.remaining)} ₽).`
        : payment.payinParts
            ? "Здесь сумма подтверждённых поступлений. Завершение доступно, когда каждая часть подтверждена либо имеет доказательство отказа. Неоплаченный остаток после завершения в пул не вернётся."
            : "Заявка закрывается одним платежом на весь остаток. Переводов может быть несколько — приложите чек на каждый.";
    const hintClass = analysis.overpay ? "danger" : "success";

    return (
        <div className="amount-field">
            <div className="amount-label-row">
                <label htmlFor="actual-amount">{isIncome ? "Сумма получена, ₽" : "Сумма перевода, ₽"}</label>
                <span className="mono">{payment.payinParts ? "Сумма заявки" : "Весь остаток"}: {formatAmount(getRemainingAmount(payment))}</span>
            </div>
            <div className="amount-input-wrap">
                <input
                    id="actual-amount"
                    type="text"
                    inputMode="decimal"
                    value={value}
                    readOnly
                    aria-readonly="true"
                />
                <span>RUB</span>
            </div>
            <div className={`amount-hint ${hintClass}`}>{hint}</div>
        </div>
    );
}

function RateInput({
                       value,
                       onChange,
                       analysis,
                       fixedRate,
                   }: {
    value: string;
    onChange: (next: string) => void;
    analysis: RateAnalysis;
    fixedRate: string | null;
}): JSX.Element {
    const locked = fixedRate != null;
    let hint = "Курс фиксируется для этого платежа при завершении.";
    let hintClass = "";
    if (locked) {
        hint = "Курс зафиксирован системой при создании заявки — изменить нельзя.";
        hintClass = "";
    } else if (value.trim() && !analysis.precisionOk) {
        hint = "Максимум два знака после запятой.";
        hintClass = "danger";
    } else if (value.trim() && !analysis.valid) {
        hint = "Курс должен быть больше 0 и не больше 10000.";
        hintClass = "danger";
    } else if (analysis.valid) {
        hint = "Курс будет отправлен в complete-запрос.";
        hintClass = "success";
    }

    return (
        <div className="amount-field">
            <div className="amount-label-row">
                <label htmlFor="currency-rate">Курс, ₽ за 1 USDT</label>
            </div>
            <div className="amount-input-wrap">
                <input
                    id="currency-rate"
                    type="text"
                    inputMode="decimal"
                    value={value}
                    disabled={locked}
                    onChange={(event) => onChange(event.target.value.replace(/[^\d.,\s]/g, ""))}
                    placeholder="80"
                />
                <span>RUB</span>
            </div>
            <div className={`amount-hint ${hintClass}`}>{hint}</div>
        </div>
    );
}
